import * as React from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { Loader2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { ImageCropper } from '@/components/ui/image-cropper';
import { Label } from '@/components/ui/label';
import { ImageFileInput } from '@/components/forms/image-file-input';
import { postMultipartImageUpload } from '@/lib/vertex-upload';
import { cn } from '@/lib/utils';

interface FormImageUploadFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  label: string;
  description?: React.ReactNode;
  required?: boolean;
  className?: string;
  /** Folder passed to `/api/upload`. */
  uploadFolder: string;
  /**
   * Aspect ratio for cropping. Defaults to `1` (square). Ignored when
   * `enableCrop` is `false`.
   */
  aspect?: number;
  /** When `false`, the image is uploaded directly without the crop step. */
  enableCrop?: boolean;
  /** Ref-friendly id so external components can trigger the file input. */
  inputId?: string;
  /** Tracks upload progress — parent forms can disable submit while uploading. */
  onUploadingChange?: (uploading: boolean) => void;
}

/**
 * Image upload field with optional cropper. The form value is the resulting
 * image URL (a string).
 */
export const FormImageUploadField = <TFieldValues extends FieldValues>({
  control,
  name,
  label,
  description,
  required,
  className,
  uploadFolder,
  aspect = 1,
  enableCrop = true,
  inputId,
  onUploadingChange,
}: FormImageUploadFieldProps<TFieldValues>) => {
  const [selectedImageFile, setSelectedImageFile] = React.useState<string | null>(null);
  const [uploading, setUploading] = React.useState(false);
  const [converting, setConverting] = React.useState(false);
  const fallbackId = React.useId();
  const fileInputId = inputId || `image-upload-${fallbackId}`;
  const isBusy = uploading || converting;

  React.useEffect(() => {
    onUploadingChange?.(isBusy);
  }, [isBusy, onUploadingChange]);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const url = (field.value as string | null | undefined) || '';

        const uploadBlob = async (blob: File | Blob) => {
          setUploading(true);
          try {
            const formData = new FormData();
            formData.append('file', blob, 'image.webp');
            formData.append('folder', uploadFolder);
            const { url } = await postMultipartImageUpload(formData);
            field.onChange(url);
            setSelectedImageFile(null);
          } catch (err) {
            console.error(err);
            toast.error(err instanceof Error ? err.message : 'Failed to upload image');
          } finally {
            setUploading(false);
          }
        };

        const onPreparedDataUrl = (dataUrl: string) => {
          setSelectedImageFile(dataUrl);
        };

        const onPrepared = async (file: File) => {
          if (!enableCrop) await uploadBlob(file);
        };

        return (
          <FormItem className={className}>
            <FormLabel>
              {label} {required && <span className="text-primary">*</span>}
            </FormLabel>
            {description && <FormDescription>{description}</FormDescription>}
            <FormControl>
              <div className="space-y-3">
                {url ? (
                  <div className="space-y-2">
                    <div className="group relative inline-block max-w-full overflow-hidden rounded-xl border border-border">
                      <img
                        src={url}
                        alt="Uploaded"
                        className="block h-auto max-h-80 max-w-full object-contain object-center"
                      />
                      {isBusy ? (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background/85 backdrop-blur-sm">
                          <Loader2 className="h-8 w-8 animate-spin text-primary" />
                          <span className="text-sm font-medium text-foreground px-4 text-center">
                            {converting ? 'Converting photo…' : 'Replacing image…'}
                          </span>
                          <span className="text-xs text-muted-foreground px-6 text-center max-w-sm">
                            Keep this tab open until the upload finishes.
                          </span>
                        </div>
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => field.onChange('')}
                          >
                            <X className="mr-2 h-4 w-4" /> Remove Image
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div
                    className={cn(
                      'rounded-xl border-2 border-dashed border-border p-6 text-center transition-colors',
                      isBusy
                        ? 'pointer-events-none border-primary/30 bg-primary/5'
                        : 'hover:bg-accent/10'
                    )}
                  >
                    <ImageFileInput
                      id={fileInputId}
                      className="hidden"
                      disabled={isBusy}
                      onPreparingChange={setConverting}
                      onPreparedDataUrl={enableCrop ? onPreparedDataUrl : undefined}
                      onPrepared={enableCrop ? undefined : onPrepared}
                    />
                    <Label
                      htmlFor={fileInputId}
                      className="flex cursor-pointer flex-col items-center gap-2"
                    >
                      {isBusy ? (
                        <>
                          <Loader2 className="h-8 w-8 animate-spin text-primary" />
                          <span className="text-sm font-semibold text-foreground">
                            {converting ? 'Converting photo…' : 'Uploading your image…'}
                          </span>
                          <span className="text-xs text-muted-foreground max-w-[280px] leading-relaxed">
                            {converting
                              ? 'Preparing this photo so it can be cropped and saved.'
                              : "Sending to secure storage. This usually takes a few seconds — please wait and don't close this page."}
                          </span>
                        </>
                      ) : (
                        <>
                          <Upload className="h-8 w-8 text-muted-foreground" />
                          <span className="text-sm font-medium text-primary">Upload Image</span>
                          <span className="text-xs text-muted-foreground">
                            Tap to choose a photo. You can crop it next.
                          </span>
                        </>
                      )}
                    </Label>
                  </div>
                )}
              </div>
            </FormControl>
            <FormMessage />

            {selectedImageFile && (
              <ImageCropper
                imageSrc={selectedImageFile}
                onCropComplete={uploadBlob}
                onCancel={() => setSelectedImageFile(null)}
                aspect={aspect}
              />
            )}
          </FormItem>
        );
      }}
    />
  );
};
FormImageUploadField.displayName = 'FormImageUploadField';
